import styles from "./Endereco.module.scss";

import React from "react";

import { useForm } from "react-hook-form";

import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { Input } from "shared/components/inputs/Input";

import { buscarEnderecoPorCep } from "shared/services/api/cep.api";

import { maskCep } from "shared/utils/masks";

/* =========================================
   VALIDAÇÃO
========================================= */

const schema = z.object({
  cep: z
    .string()
    .min(8, "CEP inválido"),

  cidade: z
    .string()
    .min(2, "Cidade obrigatória"),

  estado: z
    .string()
    .min(2, "Estado obrigatório"),

  endereco: z
    .string()
    .min(2, "Endereço obrigatório"),

  numero: z
    .string()
    .min(1, "Número obrigatório"),

  bairro: z
    .string()
    .min(2, "Bairro obrigatório"),

  /* NOVOS CAMPOS - OPCIONAIS */

  completo: z
    .string()
    .optional(),

  pontoReferencia: z
    .string()
    .optional(),
});

export type EnderecoLojaFormData =
  z.infer<typeof schema>;

/* =========================================
   PROPS
========================================= */

interface EnderecoLojaProps {
  onNext?: (
    data: EnderecoLojaFormData
  ) => void;

  defaultValues?: Partial<EnderecoLojaFormData>;

  modoEdicao?: boolean;

  lojaId?: string;

  onSalvo?: (
    dados: EnderecoLojaFormData
  ) => void;
}

/* =========================================
   COMPONENTE
========================================= */

export const Endereco: React.FC<
  EnderecoLojaProps
> = ({
  onNext,
  defaultValues,
  modoEdicao = false,
  lojaId,
  onSalvo,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EnderecoLojaFormData>({
    resolver: zodResolver(schema),

    defaultValues: {
      cep:
        defaultValues?.cep ?? "",

      cidade:
        defaultValues?.cidade ?? "",

      estado:
        defaultValues?.estado ?? "",

      endereco:
        defaultValues?.endereco ?? "",

      numero:
        defaultValues?.numero ?? "",

      bairro:
        defaultValues?.bairro ?? "",

      completo:
        defaultValues?.completo ?? "",

      pontoReferencia:
        defaultValues?.pontoReferencia ?? "",
    },
  });

  /* =========================================
     SUBMIT
  ========================================= */

  const onSubmit = (
    data: EnderecoLojaFormData
  ) => {
    if (modoEdicao && lojaId) {
      onSalvo?.(data);
      return;
    }

    onNext?.(data);
  };

  return (
    <form
      id="endereco-loja-form"
      onSubmit={handleSubmit(onSubmit)}
      className={styles.form}
    >
      {/* =================================
          CEP
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          CEP
        </label>

        <Input
          type="text"
          name="cep"
          placeholder="Digite o CEP"
          register={register("cep", {
            onChange: async (e) => {
              const value = maskCep(
                e.target.value
              );

              e.target.value = value;

              const cepLimpo =
                value.replace(/\D/g, "");

              if (cepLimpo.length === 8) {
                try {
                  const endereco =
                    await buscarEnderecoPorCep(
                      cepLimpo
                    );

                  if (endereco) {
                    setValue(
                      "endereco",
                      endereco.rua
                    );

                    setValue(
                      "bairro",
                      endereco.bairro
                    );

                    setValue(
                      "cidade",
                      endereco.cidade
                    );

                    setValue(
                      "estado",
                      endereco.estado
                    );
                  }
                } catch (error) {
                  console.error(
                    "Erro ao buscar CEP:",
                    error
                  );
                }
              }
            },
          })}
          error={errors.cep?.message}
          inputMode="numeric"
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          ENDEREÇO
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          Endereço
        </label>

        <Input
          type="text"
          name="endereco"
          placeholder="Digite o endereço"
          register={register("endereco")}
          error={errors.endereco?.message}
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          NÚMERO
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          Número
        </label>

        <Input
          type="text"
          name="numero"
          placeholder="Digite o número"
          register={register("numero")}
          error={errors.numero?.message}
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          COMPLEMENTO
      ================================= */}

      <div className={styles.containerLabel}>
        <label className={styles.label}>
          Completo
        </label>

        <Input
          type="text"
          name="completo"
          placeholder="Apartamento, bloco, sala..."
          register={register("completo")}
          error={errors.completo?.message}
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          PONTO DE REFERÊNCIA
      ================================= */}

      <div className={styles.containerLabel}>
        <label className={styles.label}>
          Ponto de Referência
        </label>

        <Input
          type="text"
          name="pontoReferencia"
          placeholder="Digite um ponto de referência"
          register={register("pontoReferencia")}
          error={
            errors.pontoReferencia?.message
          }
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          BAIRRO
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          Bairro
        </label>

        <Input
          type="text"
          name="bairro"
          placeholder="Digite o bairro"
          register={register("bairro")}
          error={errors.bairro?.message}
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          CIDADE
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          Cidade
        </label>

        <Input
          type="text"
          name="cidade"
          placeholder="Digite a cidade"
          register={register("cidade")}
          error={errors.cidade?.message}
          customClass={styles.customInput}
        />
      </div>

      {/* =================================
          ESTADO
      ================================= */}

      <div className={styles.containerLabel}>
        <label
          className={`${styles.label} ${styles.required}`}
        >
          Estado
        </label>

        <Input
          type="text"
          name="estado"
          placeholder="Digite o estado"
          register={register("estado")}
          error={errors.estado?.message}
          customClass={styles.customInput}
        />
      </div>
    </form>
  );
};